import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { Editor } from "components/shared/Editor";
import { editWork, getWork } from "store/actions";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "components/shared/Loader";
import ImageUploadEn from "./ImageUploadEn"; // Assuming this component handles individual image uploads
import SaveChangesButton from "components/shared/SaveChangesButton";
import DragDropUploader from "../../components/shared/DaragedUploader/DragDropUploader";
import { toast } from "react-toastify";

const EditWork = () => {
  const { locale } = useIntl();
  const dir = locale === "ar" ? "rtl" : "ltr";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [selectedMainImage, setSelectedMainImage] = useState({ preview: null, path: null });
  const [description, setDescription] = useState({ ar: "", en: "" });
  const [fields, setFields] = useState([]); // Images state

  const { work, loading } = useSelector((state) => state.works) || {};

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();

  // Fetch data
  useEffect(() => {
    dispatch(getWork({ page: "ourWorks", slug: slug }));
  }, [dispatch, slug]);

  // Populate form with fetched data
  useEffect(() => {
    if (work) {
      reset({
        heading: {
          ar: work?.data?.heading?.ar,
          en: work?.data?.heading?.en,
        },
        subHeading: {
          ar: work?.data?.subHeading?.ar,
          en: work?.data?.subHeading?.en,
        },
        startDate: work?.data?.startDate,
        endDate: work?.data?.endDate,
        clientName: {
          ar: work?.data?.clientName?.ar,
          en: work?.data?.clientName?.en,
        },
        location: {
          ar: work?.data?.location?.ar,
          en: work?.data?.location?.en,
        }
      });

      if (work?.data?.image) {
        setSelectedMainImage({ preview: null, path: work?.data?.image });
      }

      // Set fields for additional images
      if (work?.data?.images) {
        setFields(work?.data?.images.map(image => ({ path: image })));
      }
    }

    return () => reset({});
  }, [reset, work]);
  useEffect(() => {
    if (work) {
      setDescription({
        ar: work?.data?.description?.ar,
        en: work?.data?.description?.en,
      });
    }
  }, [work]);

  // Handle form submission
  const onSubmit = (data) => {
    if (selectedMainImage?.path) data.image = selectedMainImage.path;
    data.description = description;

    if (fields.length > 0) {
      data.images = fields
        .map((field) => field.path)
        .filter((path) => path !== null && path !== "");
    }

    const sectionData = {
      section: {
        title: slug,
        data: data,
      },
    };

    dispatch(editWork({ page: "ourWorks", slug, data: sectionData, navigate  , toast }));
  };

  // Add an empty image field
  const handleAddImage = () => {
    setFields(prevFields => [...prevFields, { path: null }]);
  };

  // Update image at a specific index
  const handleImageChange = (index, file) => {
    const updatedFields = [...fields];
    updatedFields[index] = { path: file.path };

    setFields(updatedFields);
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="acc-form">
        <div className="card">
          <div className="card-head">
            <h4>
              <FormattedMessage id="editWork" />
            </h4>
          </div>
          <div className="card-body form-group settings-tabs">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Col xl={10} md={12}>
                <Row>
                  {/* Main Image Upload */}
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="image" /></h5>
                      <ImageUploadEn
                        selectedImageEn={selectedMainImage}
                        setSelectedImageEn={setSelectedMainImage}
                        accept="image/*"
                      />
                    </div>
                  </Col>

                  {/* Additional Images Upload */}
                  <Row>

                    <Col lg={6}>
                      <h5>
                        <FormattedMessage id="multipleImages" />
                      </h5>
                    </Col>
                    <Col lg={12} className="d-flex justify-content-center w-100">
                      <DragDropUploader
                        selectedImages={fields}
                        setSelectedImages={setFields}
                      />
                    </Col>

                  </Row>
                 
                  {/* Title Inputs */}
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="title" /> (<FormattedMessage id="arabic-language" />)</h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("heading.ar", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.heading?.ar?.type === "required" && <FormattedMessage id="required" />}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="title" /> (<FormattedMessage id="english-language" />)</h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("heading.en", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.heading?.en?.type === "required" && <FormattedMessage id="required" />}
                      </p>
                    </div>
                  </Col>

                  {/* SubTitle Inputs */}
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="subTitle" /> (<FormattedMessage id="arabic-language" />)</h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("subHeading.ar", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.subHeading?.ar?.type === "required" && <FormattedMessage id="required" />}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="subTitle" /> (<FormattedMessage id="english-language" />)</h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("subHeading.en", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.subHeading?.en?.type === "required" && <FormattedMessage id="required" />}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="location" /> (
                        <FormattedMessage id="arabic-language" />)
                      </h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("location.ar", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.location?.ar?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="location" /> (
                        <FormattedMessage id="english-language" />)
                      </h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("location.en", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.location?.en?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="clientName" /> (
                        <FormattedMessage id="arabic-language" />)
                      </h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("clientName.ar", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.clientName?.ar?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="clientName" /> (
                        <FormattedMessage id="english-language" />)
                      </h5>
                      <input
                        type="text"
                        className="form-control"
                        {...register("clientName.en", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.clientName?.en?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="startDate" />
                      </h5>
                      <input
                        type="date"
                        className="form-control"
                        {...register("startDate", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.startDate?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="endDate" />
                      </h5>
                      <input
                        type="date"
                        className="form-control"
                        {...register("endDate", { required: true })}
                      />
                      <p className="error-hint">
                        {errors.endDate?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>

                  {/* Description Editors */}
                  <Col lg={12}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="description" /> (<FormattedMessage id="arabic-language" />)</h5>
                      <Controller
                        control={control}
                        name="description.ar"
                        render={() => (
                          <Editor
                            initialValue={description?.ar}
                            onChange={(e, editor) => {
                              const data = editor.getContent();
                              setDescription((prev) => ({ ...prev, ar: data }));
                            }}
                          />
                        )}
                      />
                      <p className="error-hint">
                        {errors.description?.ar?.type === "required" && <FormattedMessage id="required" />}
                      </p>
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="form-group required">
                      <h5><FormattedMessage id="description" /> (<FormattedMessage id="english-language" />)</h5>
                      <Controller
                        control={control}
                        name="description.en"
                        render={() => (
                          <Editor
                            initialValue={description?.en}
                            onChange={(e, editor) => {
                              const data = editor.getContent();
                              setDescription((prev) => ({ ...prev, en: data }));
                            }}
                          />
                        )}
                      />
                      <p className="error-hint">
                        {errors.description?.en?.type === "required" && <FormattedMessage id="required" />}
                      </p>
                    </div>
                  </Col>

                 
                    <SaveChangesButton addNew={false} />
              
                </Row>
              </Col>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditWork;
